import { useEffect, useState } from "react";
import { api, fmtNumber } from "@/lib/api";
import { downloadXlsx, todayStamp } from "@/lib/xlsx";
import { Button } from "@/components/ui/button";
import { TrendUp, TrendDown, Tag, CircleNotch, Warning, DownloadSimple } from "@phosphor-icons/react";

function RankTable({ title, rows, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
        <Icon size={20} weight="bold" style={{ color: accent }} />
        <h2 className="font-heading text-lg font-bold text-[#0F172A]">{title}</h2>
      </div>
      {rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-[#64748B]">Sin datos.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[#64748B] bg-[#F8F9FA]">
                <th className="px-5 py-3 font-semibold">#</th>
                <th className="px-5 py-3 font-semibold">Producto</th>
                <th className="px-5 py-3 font-semibold text-right">Vendido</th>
                <th className="px-5 py-3 font-semibold text-right">Vence en</th>
                <th className="px-5 py-3 font-semibold text-center">Liquidar</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                  <td className="px-5 py-3 font-bold text-[#64748B]">{i + 1}</td>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-[#0F172A]">{r.nombre}</div>
                    <div className="text-xs text-[#64748B]">{r.numero_lote}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(r.total_vendido)}</td>
                  <td className="px-5 py-3 text-right text-[#475569]">
                    {r.dias_hasta_vencimiento < 0 ? "Vencido" : `${r.dias_hasta_vencimiento}d`}
                  </td>
                  <td className="px-5 py-3 text-center">
                    {r.liquidar ? (
                      <span className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA]">
                        <Tag size={12} weight="bold" /> Sí
                      </span>
                    ) : (
                      <span className="text-xs text-[#64748B]">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Ranking() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/ranking").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  const sheetFrom = (rows) => {
    const out = [["#", "Producto", "Nº lote", "Código", "Vendido", "Stock actual", "Vence en (días)", "Liquidar"]];
    (rows || []).forEach((r, i) =>
      out.push([i + 1, r.nombre, r.numero_lote, r.codigo, r.total_vendido, r.stock_actual, r.dias_hasta_vencimiento, r.liquidar ? "Sí" : "No"]));
    return out;
  };

  const exportExcel = () => {
    const cols = [{ wch: 5 }, { wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 9 }];
    downloadXlsx(`ranking_${todayStamp()}.xlsx`, [
      { name: "Top vendidos", rows: sheetFrom(data.top_vendidos), cols },
      { name: "Menos vendidos", rows: sheetFrom(data.menos_vendidos), cols },
      { name: "Liquidación", rows: sheetFrom(data.sugerencias_liquidacion), cols },
    ]);
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="ranking-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Ranking de productos</h1>
          <p className="text-[#475569] mt-1">Rotación de los últimos 12 meses, con sugerencias de liquidación.</p>
        </div>
        <Button data-testid="export-ranking-excel" variant="outline" onClick={exportExcel} className="h-11 border-[#E2E8F0] font-semibold">
          <DownloadSimple size={18} weight="bold" className="mr-1.5" /> Exportar Excel
        </Button>
      </div>

      {data.sugerencias_liquidacion.length > 0 && (
        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-md p-5 flex items-start gap-3" data-testid="liquidacion-banner">
          <Warning size={22} weight="bold" className="text-[#9A3412] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading font-bold text-[#9A3412]">
              {data.sugerencias_liquidacion.length} producto(s) sugeridos para liquidación
            </h3>
            <p className="text-sm text-[#9A3412]/90 mt-0.5">
              Baja rotación y vencimiento próximo (&lt; 60 días):{" "}
              {data.sugerencias_liquidacion.map((s) => s.nombre).join(", ")}.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RankTable
          testid="top-vendidos-table"
          title="Top 10 más vendidos"
          rows={data.top_vendidos}
          icon={TrendUp}
          accent="#065F46"
        />
        <RankTable
          testid="menos-vendidos-table"
          title="Top 10 menos vendidos"
          rows={data.menos_vendidos}
          icon={TrendDown}
          accent="#991B1B"
        />
      </div>
    </div>
  );
}
